/**
 * Role Page Access Management Component
 *
 * Admin interface for configuring role-specific page access.
 * Allows administrators to override default page access for specific roles.
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  Users,
  Shield,
  Check,
  X,
  AlertCircle,
  Search,
  Filter,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Info,
  RotateCcw,
  Eye,
  EyeOff,
  Minus,
  Edit3,
  Trash2,
  CheckCircle,
  Download,
  Settings,
} from 'lucide-react';
import {
  useRolePagePermissions,
  useRolePagePermissionUpdate,
  usePermissionMatrix,
  useOrganizationRoles,
  clearOrgPageCapabilityCache,
  OrganizationRoleInfo,
} from '@/hooks/usePageCapability';
import {
  PageCategory,
  PAGE_CATEGORY_LABELS,
  AccessLevel,
  PageActionPermissions,
} from '@/types/pageCapabilities';
import { CBORoleType } from '@/types/cboRoles';
import { useAppTranslation } from '@/contexts/TranslationContext';

// ============================================================================
// TYPES
// ============================================================================

interface RolePageAccessManagementProps {
  organizationId: number;
  organizationName: string;
  onClose?: () => void;
  initialRoleFilter?: string | null;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function RolePageAccessManagement({
  organizationId,
  organizationName,
  onClose,
  initialRoleFilter,
}: RolePageAccessManagementProps) {
  const { t, locale } = useAppTranslation();
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<PageCategory | 'all'>('all');
  const [viewMode, setViewMode] = useState<'list' | 'matrix'>('list');
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  // Get organization-specific roles only (system roles are in the first tab)
  const {
    roles: organizationRoles,
    loading: rolesLoading,
    error: rolesError,
    refetch: refetchRoles,
  } = useOrganizationRoles(organizationId, false);

  // Set default selected role when roles load
  React.useEffect(() => {
    if (organizationRoles.length > 0 && !selectedRole) {
      setSelectedRole(organizationRoles[0].type);
    }
  }, [organizationRoles, selectedRole]);

  // Handle cross-link from unified panel: pre-select a role
  React.useEffect(() => {
    if (initialRoleFilter && organizationRoles.length > 0) {
      const match = organizationRoles.find(
        r => r.type === initialRoleFilter || r.system_role === initialRoleFilter
      );
      if (match) setSelectedRole(match.type);
    }
  }, [initialRoleFilter, organizationRoles]);

  // Load role permissions
  const {
    permissions,
    loading,
    isRefetching,
    error,
    refetch,
  } = useRolePagePermissions(
    organizationId,
    selectedRole,
    selectedCategory === 'all' ? undefined : selectedCategory
  );

  // Load permission matrix
  const {
    matrix,
    loading: matrixLoading,
    refetch: refetchMatrix,
  } = usePermissionMatrix(
    organizationId,
    selectedCategory === 'all' ? undefined : selectedCategory
  );

  // Update hooks
  const {
    updateRolePagePermission,
    bulkUpdateRolePermissions,
    resetAllRolePermissions,
    updateCategoryPermissions,
    saving,
    error: updateError,
  } = useRolePagePermissionUpdate(organizationId);

  // Group permissions by category
  const permissionsByCategory = useMemo(() => {
    const grouped: Record<string, typeof permissions> = {};
    permissions.forEach(perm => {
      const cat = perm.category || 'other';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(perm);
    });
    return grouped;
  }, [permissions]);

  // Filter by search
  const filteredPermissionsByCategory = useMemo(() => {
    if (!searchQuery.trim()) return permissionsByCategory;

    const query = searchQuery.toLowerCase();
    const filtered: Record<string, typeof permissions> = {};

    Object.entries(permissionsByCategory).forEach(([category, perms]) => {
      const matching = perms.filter(
        p =>
          p.page_name.toLowerCase().includes(query) ||
          p.page_name_es?.toLowerCase().includes(query) ||
          p.page_key.toLowerCase().includes(query)
      );
      if (matching.length > 0) {
        filtered[category] = matching;
      }
    });

    return filtered;
  }, [permissionsByCategory, searchQuery]);

  // Initialize expanded categories
  React.useEffect(() => {
    if (Object.keys(permissionsByCategory).length > 0 && Object.keys(expandedCategories).length === 0) {
      const initial: Record<string, boolean> = {};
      Object.keys(permissionsByCategory).forEach(cat => {
        initial[cat] = true;
      });
      setExpandedCategories(initial);
    }
  }, [permissionsByCategory]);

  // Get available categories
  const availableCategories = useMemo(() => {
    return [...new Set(permissions.map(p => p.category))] as PageCategory[];
  }, [permissions]);

  // Stats for current role
  const stats = useMemo(() => {
    let total = 0;
    let enabled = 0;
    let disabled = 0;
    let inherit = 0;

    permissions.forEach(p => {
      total++;
      if (p.access_level === 'enabled') enabled++;
      else if (p.access_level === 'disabled') disabled++;
      else inherit++;
    });

    return { total, enabled, disabled, inherit };
  }, [permissions]);

  // Toggle category expansion
  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  // Toggle row expansion for granular permissions
  const toggleRow = (pageKey: string) => {
    setExpandedRows(prev => ({
      ...prev,
      [pageKey]: !prev[pageKey],
    }));
  };

  // Handle access level change
  const handleAccessChange = async (pageKey: string, accessLevel: AccessLevel, currentPermissions?: PageActionPermissions) => {
    const success = await updateRolePagePermission(selectedRole, pageKey, accessLevel, currentPermissions);
    if (success) {
      refetch();
      refetchMatrix(true);
    }
  };

  // Handle granular permission change
  const handlePermissionChange = async (pageKey: string, permissionKey: keyof PageActionPermissions, value: boolean, currentPerm: any) => {
    const currentPermissions: PageActionPermissions = currentPerm.permissions || {};
    const updatedPermissions: PageActionPermissions = {
      ...currentPermissions,
      [permissionKey]: value,
    };

    // If we're enabling a granular permission, make sure access_level is at least 'enabled'
    const newAccessLevel = currentPerm.access_level === 'disabled' ? 'enabled' : currentPerm.access_level;

    const success = await updateRolePagePermission(selectedRole, pageKey, newAccessLevel, updatedPermissions);
    if (success) {
      refetch();
      refetchMatrix(true);
    }
  };

  // Handle reset all for current role
  const handleResetAll = async () => {
    if (!confirm(t('admin.confirmResetAllRole', 'Are you sure you want to reset all page permissions for this role?'))) {
      return;
    }
    const success = await resetAllRolePermissions(selectedRole);
    if (success) {
      refetch();
      refetchMatrix(true);
    }
  };

  // Handle category-level permission update
  const handleCategoryPermissionChange = async (category: string, accessLevel: 'enabled' | 'disabled' | 'inherit') => {
    const actionText = accessLevel === 'enabled' ? 'allow' : accessLevel === 'disabled' ? 'deny' : 'reset to default';
    const categoryLabel = getCategoryLabel(category as PageCategory);
    const categoryPerms = permissionsByCategory[category] || [];

    // TRUS-446: the message was a template literal, so its text could never be
    // translated — i18next only ever saw the already-interpolated string. Moved
    // the values into interpolation options so es.json can carry the sentence.
    const categoryUpdatePrompt = t(
      'admin.confirmCategoryUpdate',
      'Are you sure you want to {{action}} all {{count}} pages in "{{category}}" for this role?',
      { action: actionText, count: categoryPerms.length, category: categoryLabel }
    );
    if (!confirm(categoryUpdatePrompt)) {
      return;
    }

    const result = await updateCategoryPermissions(selectedRole, category, accessLevel);
    if (result.success) {
      refetch();
      refetchMatrix(true);
    }
  };

  // Get category permission summary (for showing current state)
  const getCategoryPermissionSummary = (category: string) => {
    const perms = permissionsByCategory[category] || [];
    const enabled = perms.filter(p => p.access_level === 'enabled').length;
    const disabled = perms.filter(p => p.access_level === 'disabled').length;
    const inherited = perms.length - enabled - disabled;

    if (disabled === perms.length) return 'all_disabled';
    if (enabled === perms.length) return 'all_enabled';
    if (inherited === perms.length) return 'all_inherited';
    return 'mixed';
  };

  // Get role info (from organization roles)
  const getRoleInfo = (roleType: string): OrganizationRoleInfo | undefined => {
    return organizationRoles.find(r => r.type === roleType);
  };

  // Get category label
  const getCategoryLabel = (category: PageCategory) => {
    const labels = PAGE_CATEGORY_LABELS[category];
    return locale === 'es' ? labels?.es : labels?.en || category;
  };

  // Get page name
  const getPageName = (permission: any) => {
    return locale === 'es' && permission.page_name_es ? permission.page_name_es : permission.page_name;
  };

  // Access level button component
  const AccessButton = ({
    level,
    current,
    onClick,
    disabled,
  }: {
    level: AccessLevel;
    current: AccessLevel;
    onClick: () => void;
    disabled?: boolean;
  }) => {
    const isActive = current === level;

    const getStyles = () => {
      if (!isActive) {
        return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600';
      }
      switch (level) {
        case 'enabled':
          return 'bg-green-500 text-white';
        case 'disabled':
          return 'bg-red-500 text-white';
        default:
          return 'bg-gray-500 text-white';
      }
    };

    const getLabel = () => {
      switch (level) {
        case 'enabled':
          return t('admin.allow', 'Allow');
        case 'disabled':
          return t('admin.deny', 'Deny');
        default:
          return t('admin.inherit', 'Default');
      }
    };

    const getIcon = () => {
      switch (level) {
        case 'enabled':
          return <Check className="w-3 h-3" />;
        case 'disabled':
          return <X className="w-3 h-3" />;
        default:
          return <Minus className="w-3 h-3" />;
      }
    };

    return (
      <button
        onClick={onClick}
        disabled={disabled}
        className={`flex items-center gap-1 px-2 py-1 text-xs font-medium rounded transition-colors ${getStyles()} ${
          disabled ? 'opacity-50 cursor-not-allowed' : ''
        }`}
      >
        {getIcon()}
        <span>{getLabel()}</span>
      </button>
    );
  };

  // Granular permission checkbox component
  const PermissionCheckbox = ({
    label,
    labelEs,
    permKey,
    checked,
    onChange,
    disabled,
    icon: Icon,
  }: {
    label: string;
    labelEs?: string;
    permKey: keyof PageActionPermissions;
    checked: boolean;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
    icon: React.ComponentType<{ className?: string }>;
  }) => {
    const displayLabel = locale === 'es' && labelEs ? labelEs : label;

    return (
      <label className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors cursor-pointer ${
        checked
          ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-300 dark:border-purple-700'
          : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
          className="w-4 h-4 text-purple-600 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500 focus:ring-2"
        />
        <Icon className={`w-4 h-4 ${checked ? 'text-purple-600 dark:text-purple-400' : 'text-gray-500 dark:text-gray-400'}`} />
        <span className={`text-sm font-medium ${checked ? 'text-purple-700 dark:text-purple-300' : 'text-gray-700 dark:text-gray-300'}`}>
          {displayLabel}
        </span>
      </label>
    );
  };

  // Granular permissions panel
  const GranularPermissionsPanel = ({ perm }: { perm: any }) => {
    const currentPermissions: PageActionPermissions = perm.permissions || {};
    const isDisabled = saving || perm.access_level === 'disabled';

    return (
      <div className="mt-3 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2 mb-3">
          <Settings className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('admin.granularPermissions', 'Granular Permissions')}
          </span>
          {perm.access_level === 'disabled' && (
            <span className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded">
              {t('admin.enableAccessFirst', 'Enable access first to configure')}
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
          <PermissionCheckbox
            label="View"
            labelEs="Ver"
            permKey="view"
            checked={currentPermissions.view !== false}
            onChange={(checked) => handlePermissionChange(perm.page_key, 'view', checked, perm)}
            disabled={isDisabled}
            icon={Eye}
          />
          <PermissionCheckbox
            label="Edit"
            labelEs="Editar"
            permKey="edit"
            checked={currentPermissions.edit === true}
            onChange={(checked) => handlePermissionChange(perm.page_key, 'edit', checked, perm)}
            disabled={isDisabled}
            icon={Edit3}
          />
          <PermissionCheckbox
            label="Delete"
            labelEs="Eliminar"
            permKey="delete"
            checked={currentPermissions.delete === true}
            onChange={(checked) => handlePermissionChange(perm.page_key, 'delete', checked, perm)}
            disabled={isDisabled}
            icon={Trash2}
          />
          <PermissionCheckbox
            label="Approve"
            labelEs="Aprobar"
            permKey="approve"
            checked={currentPermissions.approve === true}
            onChange={(checked) => handlePermissionChange(perm.page_key, 'approve', checked, perm)}
            disabled={isDisabled}
            icon={CheckCircle}
          />
          <PermissionCheckbox
            label="Export"
            labelEs="Exportar"
            permKey="export"
            checked={currentPermissions.export === true}
            onChange={(checked) => handlePermissionChange(perm.page_key, 'export', checked, perm)}
            disabled={isDisabled}
            icon={Download}
          />
        </div>
        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
          {t('admin.granularPermissionsHint', 'View is enabled by default when access is allowed. Other permissions must be explicitly granted.')}
        </p>
      </div>
    );
  };

  // Loading state - wait for roles and permissions
  if (loading || rolesLoading || !selectedRole) {
    return (
      <div className="role-page-access-management bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="flex items-center justify-center p-12">
          <div className="flex flex-col items-center gap-4">
            <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
            <p className="text-gray-500 dark:text-gray-400">
              {t('admin.loadingRolePermissions', 'Loading role permissions...')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="role-page-access-management bg-white dark:bg-gray-800 rounded-lg shadow relative">
      {/* Subtle updating indicator - doesn't reset scroll */}
      {(isRefetching || saving) && (
        <div className="absolute top-0 left-0 right-0 h-1 bg-indigo-100 dark:bg-indigo-900/30 overflow-hidden z-10">
          <div className="h-full bg-indigo-500 animate-pulse" style={{ width: '100%' }} />
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
            <Shield className="w-6 h-6 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t('admin.rolePageAccess', 'Role-Based Page Access')}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">{organizationName}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              clearOrgPageCapabilityCache(organizationId);
              refetch();
              refetchMatrix(true);
            }}
            className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title={t('common.refresh', 'Refresh')}
          >
            <RefreshCw className="w-5 h-5" />
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Role Selector */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
        {rolesLoading ? (
          <div className="flex items-center gap-2 text-gray-500">
            <RefreshCw className="w-4 h-4 animate-spin" />
            <span className="text-sm">{t('common.loading', 'Loading roles...')}</span>
          </div>
        ) : rolesError ? (
          <div className="flex items-center gap-2 text-red-500">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm">{rolesError}</span>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-2">
              {organizationRoles.map(role => {
                const isSelected = selectedRole === role.type;
                return (
                  <button
                    key={role.type}
                    onClick={() => setSelectedRole(role.type)}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors flex items-center gap-2 ${
                      isSelected
                        ? 'bg-purple-600 text-white'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    {locale === 'es' ? (role.display_name_es || role.display_name) : role.display_name}
                    {role.is_organization_role && (
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        isSelected ? 'bg-purple-500' : 'bg-gray-200 dark:bg-gray-700'
                      }`}>
                        {role.abbreviation || role.system_role?.substring(0, 3).toUpperCase()}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Role Description */}
            {selectedRole && getRoleInfo(selectedRole) && (
              <div className="mt-4 p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                <div className="flex items-start gap-3">
                  <Users className="w-5 h-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-purple-800 dark:text-purple-200">
                        {locale === 'es'
                          ? (getRoleInfo(selectedRole)?.display_name_es || getRoleInfo(selectedRole)?.display_name)
                          : getRoleInfo(selectedRole)?.display_name}
                      </h4>
                      {getRoleInfo(selectedRole)?.is_organization_role && (
                        <span className="text-xs px-2 py-0.5 rounded bg-purple-200 dark:bg-purple-800 text-purple-700 dark:text-purple-300">
                          {t('admin.orgRole', 'Org Role')}
                        </span>
                      )}
                      {getRoleInfo(selectedRole)?.system_role && (
                        <span className="text-xs px-2 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                          {getRoleInfo(selectedRole)?.system_role}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-purple-600 dark:text-purple-300 mt-1">
                      {getRoleInfo(selectedRole)?.description || t('admin.noDescription', 'No description available')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Member edit access (data-level) — enforced by DataPermissions,
                independent of the page toggles below (TRUS-283). */}
            {selectedRole && getRoleInfo(selectedRole) && (() => {
              const sysRole = getRoleInfo(selectedRole)?.system_role;
              const orgWideEdit = ['super_admin', 'org_admin', 'ops_admin', 'program_manager', 'supervisor']
                .includes(sysRole || '');
              return (
                <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="flex items-start gap-3">
                    {orgWideEdit ? (
                      <Edit3 className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    ) : (
                      <Eye className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <h4 className="font-medium text-blue-800 dark:text-blue-200 text-sm">
                        {t('admin.memberEditAccess', 'Member edit access')}
                      </h4>
                      <p className="text-sm text-blue-600 dark:text-blue-300 mt-1">
                        {orgWideEdit
                          ? t(
                              'admin.memberEditAccessPrivileged',
                              'This role can view and edit every member in the organization. Page toggles below control which pages are visible, not member edit rights.'
                            )
                          : t(
                              'admin.memberEditAccessScoped',
                              'This role can only edit members assigned to them: a direct member assignment, being the assigned case manager or primary coordinator, or a program assignment with "Can manage members" enabled (set in User Management → Assignments). All other reachable members are view-only. This rule is enforced at the data level and is separate from the page toggles below.'
                            )}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })()}
          </>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {t('admin.totalPages', 'Total Pages')}
          </div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">{stats.enabled}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {t('admin.overrideAllow', 'Allow Overrides')}
          </div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-red-600">{stats.disabled}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {t('admin.overrideDeny', 'Deny Overrides')}
          </div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-500">{stats.inherit}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {t('admin.usingDefault', 'Using Default')}
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4 p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder={t('admin.searchPages', 'Search pages...')}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>

        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-gray-400" />
          <select
            value={selectedCategory}
            onChange={e => setSelectedCategory(e.target.value as PageCategory | 'all')}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="all">{t('admin.allCategories', 'All Categories')}</option>
            {availableCategories.map(cat => (
              <option key={cat} value={cat}>
                {getCategoryLabel(cat)}
              </option>
            ))}
          </select>
        </div>

        {/* View mode toggle */}
        <div className="flex items-center border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
          <button
            onClick={() => setViewMode('list')}
            className={`px-3 py-2 text-sm font-medium transition-colors ${
              viewMode === 'list'
                ? 'bg-purple-600 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            {t('admin.listView', 'List')}
          </button>
          <button
            onClick={() => setViewMode('matrix')}
            className={`px-3 py-2 text-sm font-medium transition-colors ${
              viewMode === 'matrix'
                ? 'bg-purple-600 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            {t('admin.matrixView', 'Matrix')}
          </button>
        </div>

        <button
          onClick={handleResetAll}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
        >
          <RotateCcw className="w-4 h-4" />
          {t('admin.resetAll', 'Reset All')}
        </button>
      </div>

      {/* Error message */}
      {(error || updateError) && (
        <div className="mx-4 mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-400">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error || updateError}</span>
        </div>
      )}

      {/* Matrix View */}
      {viewMode === 'matrix' && (
        <div className="p-4 overflow-x-auto">
          {matrixLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
            </div>
          ) : matrix && ((matrix.roles && matrix.roles.length > 0) || (matrix.matrix && matrix.matrix.length > 0)) ? (
            <>
              {/* TRUS-562: the table scrolls inside its own box so the role names in
                  the header stay visible however far down the page list you go. */}
              <div className="overflow-auto max-h-[70vh]">
              <table className="w-full text-xs border-collapse min-w-[600px]">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-900/50">
                    <th className="text-left px-3 py-3 font-medium text-gray-600 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700 sticky left-0 top-0 bg-gray-50 dark:bg-gray-900 z-30 min-w-[200px]">
                      Page
                    </th>
                    {matrix.roles.map((role: any) => {
                      const roleType = typeof role === 'string' ? role : role.type;
                      const roleName = typeof role === 'string' ? role.replace(/_/g, ' ') : (role.display_name || role.type);
                      const systemRole = typeof role === 'string' ? null : role.system_role;
                      return (
                        <th
                          key={roleType}
                          className="text-center px-2 py-3 font-medium text-gray-600 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700 min-w-[90px] sticky top-0 z-20 bg-gray-50 dark:bg-gray-900"
                        >
                          <div className="flex flex-col items-center gap-1">
                            <span className="truncate max-w-[80px]">
                              {roleName}
                            </span>
                            {systemRole && (
                              <span className="text-[10px] px-1 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                                {systemRole}
                              </span>
                            )}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Group matrix rows by category
                    const byCategory: Record<string, typeof matrix.matrix> = {};
                    matrix.matrix.forEach((row: any) => {
                      const cat = row.category || 'other';
                      if (!byCategory[cat]) byCategory[cat] = [];
                      byCategory[cat].push(row);
                    });

                    // Filter by search
                    const query = searchQuery.toLowerCase();

                    return Object.entries(byCategory).map(([category, rows]) => {
                      const filteredRows = query
                        ? rows.filter((r: any) =>
                            r.page_name?.toLowerCase().includes(query) ||
                            r.page_key?.toLowerCase().includes(query)
                          )
                        : rows;

                      if (filteredRows.length === 0) return null;

                      return (
                        <React.Fragment key={category}>
                          {/* Category header row */}
                          <tr>
                            <td
                              colSpan={matrix.roles.length + 1}
                              className="px-3 py-2 bg-gray-100 dark:bg-gray-800 font-semibold text-sm text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700"
                            >
                              {getCategoryLabel(category as PageCategory)}
                              <span className="text-xs font-normal text-gray-500 ml-2">({filteredRows.length})</span>
                            </td>
                          </tr>
                          {filteredRows.map((row: any) => (
                            <tr
                              key={row.page_key}
                              className="border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                            >
                              <td className="px-3 py-2 text-gray-700 dark:text-gray-300 sticky left-0 bg-white dark:bg-gray-800 z-10">
                                <div className="flex items-center gap-1">
                                  <span>{locale === 'es' && row.page_name_es ? row.page_name_es : row.page_name}</span>
                                  {!row.org_enabled && (
                                    <span className="text-[10px] px-1 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-400" title="Disabled at org level">
                                      OFF
                                    </span>
                                  )}
                                </div>
                              </td>
                              {matrix.roles.map((role: any) => {
                                const roleType = typeof role === 'string' ? role : role.type;
                                const entry = row.roles?.[roleType];
                                if (!entry) {
                                  return (
                                    <td key={roleType} className="px-2 py-2 text-center">
                                      <span className="text-gray-300">—</span>
                                    </td>
                                  );
                                }

                                const bgColor = !row.org_enabled
                                  ? 'bg-gray-100 dark:bg-gray-900/30'
                                  : entry.effective_access
                                  ? entry.access_level === 'enabled'
                                    ? 'bg-green-100 dark:bg-green-900/30'
                                    : 'bg-green-50 dark:bg-green-900/10'
                                  : entry.access_level === 'disabled'
                                  ? 'bg-red-100 dark:bg-red-900/30'
                                  : entry.is_excluded_by_default
                                  ? 'bg-orange-50 dark:bg-orange-900/10'
                                  : 'bg-red-50 dark:bg-red-900/10';

                                const textColor = !row.org_enabled
                                  ? 'text-gray-400'
                                  : entry.effective_access
                                  ? 'text-green-700 dark:text-green-400'
                                  : 'text-red-600 dark:text-red-400';

                                const label = !row.org_enabled
                                  ? '—'
                                  : entry.access_level === 'enabled'
                                  ? '✓'
                                  : entry.access_level === 'disabled'
                                  ? '✗'
                                  : entry.effective_access
                                  ? '·'
                                  : entry.is_excluded_by_default
                                  ? '⊘'
                                  : '·';

                                return (
                                  <td key={roleType} className="px-1 py-1 text-center">
                                    <span
                                      className={`inline-flex items-center justify-center w-7 h-7 rounded text-sm font-bold ${bgColor} ${textColor}`}
                                      title={
                                        !row.org_enabled
                                          ? 'Disabled at org level'
                                          : `${entry.access_level} (${entry.effective_access ? 'accessible' : 'blocked'})${entry.is_excluded_by_default ? ' — excluded by default' : ''}`
                                      }
                                    >
                                      {label}
                                    </span>
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    });
                  })()}
                </tbody>
              </table>
              </div>

              {/* Matrix legend */}
              <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                <div className="flex items-center gap-1.5">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-[11px] font-bold">✓</span>
                  <span>Explicitly allowed</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-[11px] font-bold">✗</span>
                  <span>Explicitly denied</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-green-50 dark:bg-green-900/10 text-green-700 dark:text-green-400 text-[11px] font-bold">·</span>
                  <span>Inherited (accessible)</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-orange-50 dark:bg-orange-900/10 text-red-600 dark:text-red-400 text-[11px] font-bold">⊘</span>
                  <span>Excluded by default</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-gray-100 dark:bg-gray-900/30 text-gray-400 text-[11px]">—</span>
                  <span>Org-level disabled</span>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <p>{t('admin.noMatrixData', 'No permission matrix data available.')}</p>
              <p className="text-xs mt-2">
                {matrix === null ? 'Matrix is null (fetch may have failed)' :
                 !matrix.roles ? `No roles array. Keys: [${Object.keys(matrix || {}).join(', ')}]. Type: ${typeof matrix}. Sample: ${JSON.stringify(matrix).substring(0, 200)}` :
                 matrix.roles.length === 0 ? 'Roles array is empty (no non-admin roles configured for this org)' :
                 !matrix.matrix ? 'No matrix array in response' :
                 matrix.matrix.length === 0 ? 'Matrix rows are empty (no pages)' :
                 `Unknown: roles=${JSON.stringify(matrix.roles?.length)}, matrix=${JSON.stringify(matrix.matrix?.length)}`}
              </p>
              <button
                onClick={() => refetchMatrix()}
                className="mt-3 px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                Retry
              </button>
            </div>
          )}
        </div>
      )}

      {/* Permission List (shown when viewMode === 'list') */}
      {viewMode === 'list' && (
      <div className="p-4 space-y-4 max-h-[500px] overflow-y-auto">
        {Object.entries(filteredPermissionsByCategory).map(([category, perms]) => {
          const categoryStatus = getCategoryPermissionSummary(category);

          return (
          <div key={category} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            {/* Category Header */}
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900/50">
              <button
                onClick={() => toggleCategory(category)}
                className="flex items-center gap-3 hover:bg-gray-100 dark:hover:bg-gray-900/70 -m-2 p-2 rounded-lg transition-colors"
              >
                {expandedCategories[category] ? (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-500" />
                )}
                <span className="font-medium text-gray-900 dark:text-white">
                  {getCategoryLabel(category as PageCategory)}
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  ({perms.length} {t('admin.pages', 'pages')})
                </span>
                {/* Category status indicator */}
                {categoryStatus === 'all_enabled' && (
                  <span className="px-2 py-0.5 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded">
                    {t('admin.allAllowed', 'All Allowed')}
                  </span>
                )}
                {categoryStatus === 'all_disabled' && (
                  <span className="px-2 py-0.5 text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded">
                    {t('admin.allDenied', 'All Denied')}
                  </span>
                )}
                {categoryStatus === 'mixed' && (
                  <span className="px-2 py-0.5 text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded">
                    {t('admin.mixed', 'Mixed')}
                  </span>
                )}
              </button>

              {/* Category-level quick actions */}
              <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => handleCategoryPermissionChange(category, 'enabled')}
                  disabled={saving || categoryStatus === 'all_enabled'}
                  className={`flex items-center gap-1 px-2 py-1 text-xs font-medium rounded transition-colors ${
                    categoryStatus === 'all_enabled'
                      ? 'bg-green-500 text-white cursor-default'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-green-100 dark:hover:bg-green-900/30 hover:text-green-700 dark:hover:text-green-300'
                  } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title={t('admin.allowAllInCategory', 'Allow all pages in this category')}
                >
                  <Check className="w-3 h-3" />
                  <span>{t('admin.allowAll', 'Allow All')}</span>
                </button>
                <button
                  onClick={() => handleCategoryPermissionChange(category, 'disabled')}
                  disabled={saving || categoryStatus === 'all_disabled'}
                  className={`flex items-center gap-1 px-2 py-1 text-xs font-medium rounded transition-colors ${
                    categoryStatus === 'all_disabled'
                      ? 'bg-red-500 text-white cursor-default'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-red-100 dark:hover:bg-red-900/30 hover:text-red-700 dark:hover:text-red-300'
                  } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title={t('admin.denyAllInCategory', 'Deny all pages in this category')}
                >
                  <X className="w-3 h-3" />
                  <span>{t('admin.denyAll', 'Deny All')}</span>
                </button>
                <button
                  onClick={() => handleCategoryPermissionChange(category, 'inherit')}
                  disabled={saving || categoryStatus === 'all_inherited'}
                  className={`flex items-center gap-1 px-2 py-1 text-xs font-medium rounded transition-colors ${
                    categoryStatus === 'all_inherited'
                      ? 'bg-gray-500 text-white cursor-default'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                  } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title={t('admin.resetCategoryToDefault', 'Reset all pages in this category to default')}
                >
                  <RotateCcw className="w-3 h-3" />
                  <span>{t('admin.reset', 'Reset')}</span>
                </button>
              </div>
            </div>

            {/* Permission Items */}
            {expandedCategories[category] && (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {perms.map(perm => {
                  const isRowExpanded = expandedRows[perm.page_key];
                  const hasGranularPerms = perm.permissions && Object.keys(perm.permissions).length > 0;
                  const granularCount = hasGranularPerms
                    ? Object.values(perm.permissions as PageActionPermissions).filter(v => v === true).length
                    : 0;

                  return (
                    <div
                      key={perm.page_key}
                      className={`p-4 ${
                        perm.is_excluded_by_default
                          ? 'bg-amber-50/50 dark:bg-amber-900/10'
                          : perm.effective_access
                          ? 'bg-white dark:bg-gray-800'
                          : 'bg-gray-50 dark:bg-gray-900/30'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-4">
                        {/* Page Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-gray-900 dark:text-white">
                              {getPageName(perm)}
                            </span>
                            {perm.is_excluded_by_default && (
                              <span className="px-2 py-0.5 text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded">
                                {t('admin.excludedByDefault', 'Excluded by Default')}
                              </span>
                            )}
                            {perm.access_level !== 'inherit' && (
                              <span className={`px-2 py-0.5 text-xs rounded ${
                                perm.access_level === 'enabled'
                                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                                  : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                              }`}>
                                {t('admin.override', 'Override')}
                              </span>
                            )}
                            {granularCount > 0 && (
                              <span className="px-2 py-0.5 text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded">
                                {granularCount} {t('admin.permissions', 'permissions')}
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {perm.page_path}
                          </div>

                          {/* Effective access indicator */}
                          <div className="mt-2 flex items-center gap-2 text-sm">
                            {perm.effective_access ? (
                              <>
                                <Eye className="w-4 h-4 text-green-500" />
                                <span className="text-green-600 dark:text-green-400">
                                  {t('admin.hasAccess', 'Has Access')}
                                </span>
                              </>
                            ) : (
                              <>
                                <EyeOff className="w-4 h-4 text-red-500" />
                                <span className="text-red-600 dark:text-red-400">
                                  {t('admin.noAccess', 'No Access')}
                                </span>
                              </>
                            )}
                            {!perm.org_page_enabled && perm.org_page_enabled !== null && (
                              <span className="text-gray-400 dark:text-gray-500">
                                ({t('admin.disabledAtOrgLevel', 'Disabled at org level')})
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Access Level Buttons and Expand */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <div className="flex items-center gap-1">
                            <AccessButton
                              level="inherit"
                              current={perm.access_level}
                              onClick={() => handleAccessChange(perm.page_key, 'inherit', perm.permissions)}
                              disabled={saving}
                            />
                            <AccessButton
                              level="enabled"
                              current={perm.access_level}
                              onClick={() => handleAccessChange(perm.page_key, 'enabled', perm.permissions)}
                              disabled={saving}
                            />
                            <AccessButton
                              level="disabled"
                              current={perm.access_level}
                              onClick={() => handleAccessChange(perm.page_key, 'disabled', perm.permissions)}
                              disabled={saving}
                            />
                          </div>
                          <button
                            onClick={() => toggleRow(perm.page_key)}
                            className={`p-2 rounded-lg transition-colors ${
                              isRowExpanded
                                ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
                                : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`}
                            title={t('admin.configurePermissions', 'Configure granular permissions')}
                          >
                            <Settings className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Granular Permissions Panel */}
                      {isRowExpanded && <GranularPermissionsPanel perm={perm} />}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
        })}

        {/* Empty state */}
        {Object.keys(filteredPermissionsByCategory).length === 0 && (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            {searchQuery
              ? t('admin.noMatchingPages', 'No pages match your search.')
              : t('admin.noPagesFound', 'No pages found.')}
          </div>
        )}
      </div>
      )}

      {/* Footer with legend */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-gray-500 rounded" />
            <span>{t('admin.legendInherit', 'Default - Uses organization setting')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 rounded" />
            <span>{t('admin.legendAllow', 'Allow - Always grant access')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-red-500 rounded" />
            <span>{t('admin.legendDeny', 'Deny - Always deny access')}</span>
          </div>
          <div className="flex items-center gap-2">
            <Settings className="w-3 h-3 text-purple-500" />
            <span>{t('admin.legendGranular', 'Click gear icon for View/Edit/Delete/Approve/Export permissions')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RolePageAccessManagement;
